import React from 'react';
import { Box, Container, Divider, Link } from '@mui/material';
import Typography from '../components/i18n/LocalizedTypography';

const WEBSITE_NAME = 'Kundlistar Studio';
const WEBSITE_URL = 'https://kundlistarstudio.com';
const CONTACT_EMAIL = 'info@kundlistarstudio.com';
const CONTACT_PHONE = '+91 8284927820';
const CONTACT_ADDRESS = 'India';

const PrivacyPolicy: React.FC = () => {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: 07 July 2026
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            This Privacy Policy explains how {WEBSITE_NAME} ("we", "us", "our") collects, uses,
            stores, shares, and protects your personal information when you visit or use our website,
            iPhone or iPad application, and related services at{' '}
            <Link href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">{WEBSITE_URL}</Link>.
          </Typography>
        </Box>

        <Divider sx={{ mb: 5 }} />

        <Section title="1. Information We Collect">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Personal Information You Provide
          </Typography>
          <BulletList items={[
            'Name, email address, phone number, billing details, and account information.',
            'Birth details, chart details, client-management records, planner notes, report inputs, and other information you voluntarily save or submit.',
            'Messages, enquiries, feedback, support requests, and form submissions.',
            'Payment-related information needed to manage purchases, invoices, refunds, or subscriptions.',
          ]} />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Automatically Collected Information
          </Typography>
          <BulletList items={[
            'IP address, browser type, device type, operating system, pages visited, time and date of visit, referring website, approximate location, and cookies or similar tracking data.',
            'Usage, performance, security, and diagnostic data used to operate and improve the website and services.',
            'App events such as installs, opens, registrations, subscriptions, purchases, and feature interactions.',
            'The Apple advertising identifier (IDFA) only when you grant permission through Apple’s App Tracking Transparency prompt.',
          ]} />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Payment Information
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            If payments are made through our website, payment details may be processed by third-party
            payment gateways. We do not store full card numbers, UPI PINs, CVV codes, or banking
            passwords on our servers.
          </Typography>
        </Section>

        <Section title="2. How We Use Your Information">
          <BulletList items={[
            'To provide, operate, and improve our website and services.',
            'To calculate charts, generate astrology reports, save client records, and support planner or workspace features.',
            'To respond to enquiries, requests, or customer support messages.',
            'To process payments, invoices, refunds, subscriptions, or service requests.',
            'To send important service, account, security, or transactional updates.',
            'To send marketing or promotional communications where permitted by law.',
            'To monitor website performance, security, and usage.',
            'To measure app installs, app events, subscriptions, and advertising campaign performance.',
            'To prevent fraud, misuse, unauthorized access, or illegal activity.',
            'To comply with legal, regulatory, tax, accounting, or contractual obligations.',
          ]} />
        </Section>

        <Section title="3. Cookies and Tracking Technologies">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Our website and mobile applications may use cookies, pixels, software development kits,
            analytics tools, local storage, and similar technologies to improve performance, remember
            preferences, analyze traffic, measure app installs and marketing performance, and provide
            a better experience. This includes Meta App Events for app-install and campaign
            measurement.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            On iPhone and iPad, access to the IDFA is requested through Apple’s App Tracking
            Transparency prompt. You can deny that request or change the permission later in iOS
            Settings. You can disable cookies through your browser settings. Some parts of the
            services may not function properly if these technologies are disabled.
          </Typography>
        </Section>

        <Section title="4. Sharing of Information">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            We do not sell your personal information. We may share information with trusted third
            parties only when necessary, including:
          </Typography>
          <BulletList items={[
            'Payment gateway providers.',
            'Hosting, cloud, database, analytics, advertising-measurement, and security providers, including Meta Platforms where App Events are enabled.',
            'Email, communication, CRM, and customer-support tools.',
            'AI or report-generation service providers where needed to provide selected features.',
            'Professional advisors such as accountants, auditors, or legal consultants.',
            'Government authorities, regulators, or law enforcement agencies when required by law.',
          ]} />
        </Section>

        <Section title="5. Data Retention">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We retain personal information only for as long as necessary to fulfill the purposes
            described in this Privacy Policy, including legal, tax, accounting, security, billing,
            dispute-resolution, and business requirements. When information is no longer required, we
            may delete, anonymize, or securely archive it.
          </Typography>
        </Section>

        <Section title="6. Data Security">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We use reasonable technical and organizational measures to protect your information
            against unauthorized access, loss, misuse, alteration, or disclosure. However, no method of
            transmission over the internet or electronic storage is completely secure, so we cannot
            guarantee absolute security.
          </Typography>
        </Section>

        <Section title="7. Your Rights">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Depending on applicable law, you may have the right to:
          </Typography>
          <BulletList items={[
            'Access the personal information we hold about you.',
            'Request correction of inaccurate or incomplete information.',
            'Request deletion or erasure of your personal information.',
            'Withdraw consent where processing is based on consent.',
            'Object to or restrict certain types of processing.',
            'Request information about how your personal data is processed.',
            'Raise a grievance regarding the handling of your personal information.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            To exercise these rights, contact us at{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
          </Typography>
        </Section>

        <Section title="8. Withdrawal of Consent">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Where we process your personal information based on consent, you may withdraw consent at
            any time by contacting us. Withdrawal may affect our ability to provide certain services
            and will not affect processing already carried out before withdrawal or processing
            required by applicable law.
          </Typography>
        </Section>

        <Section title="9. Marketing Communications">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We may send promotional emails, offers, newsletters, or updates if you have opted in or
            where permitted by law. You can opt out by using the unsubscribe link in our emails or by
            contacting us. We may still send non-promotional account, transaction, service, security,
            or legal notices.
          </Typography>
        </Section>

        <Section title="10. Children's Privacy">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Our website and services are not intended for children under 18. We do not knowingly
            collect personal information from children. If you believe a child has provided personal
            information, please contact us and we will take appropriate steps to delete it.
          </Typography>
        </Section>

        <Section title="11. Third-Party Links">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Our website may contain links to third-party websites, tools, or services. We are not
            responsible for the privacy practices, content, or security of third-party websites. You
            should review the privacy policies of any third-party websites you visit.
          </Typography>
        </Section>

        <Section title="12. International Data Transfers">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Your information may be processed or stored in countries other than your own, depending on
            the location of our service providers, hosting providers, or business operations. Where
            required by applicable law, we will take appropriate steps to protect your information
            during such transfers.
          </Typography>
        </Section>

        <Section title="13. Updates to This Privacy Policy">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with an updated "Last Updated" date. Your continued use of our website or services after
            changes are posted means you accept the updated Privacy Policy.
          </Typography>
        </Section>

        <Section title="14. Contact Us">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
            For questions, requests, or complaints regarding this Privacy Policy or your personal
            information, contact us at:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1">{WEBSITE_NAME}</Typography>
            <Typography variant="body1">
              Email: <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
            </Typography>
            <Typography variant="body1">Phone: {CONTACT_PHONE}</Typography>
            <Typography variant="body1">Website: {WEBSITE_URL}</Typography>
            <Typography variant="body1">Address: {CONTACT_ADDRESS}</Typography>
            <Typography variant="body1">Grievance/Privacy Contact: Kundlistar Studio Support</Typography>
          </Box>
        </Section>

        <Divider sx={{ my: 5 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} {WEBSITE_NAME}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ pl: 0.5 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default PrivacyPolicy;
